import {
  authStorageInitialState,
  useGetMeQuery,
  useGetUserQuery,
  useLoginMutation,
  UsersPermissionsUser
} from "@mc-intranet/shared";
import { PersonOutlineOutlined } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Container,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material/";
import { useAtom } from "jotai";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { authStorageAtom } from "../../atoms";
import { useGraphQLClient } from "../../cache";
import Loading from "../../components/ui/Loading";
import { PasswordField } from "../../components/ui/PasswordField";

type FormData = {
  userName: string;
  password: string;
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Props {
  // loading: boolean;
  // login: (userName: string, password: string) => void;
}

const LoginPage: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || "/home";

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm<FormData>({
    defaultValues: {
      userName: "",
      password: ""
    }
  });

  const [authStorage, setAuthStorage] = useAtom(authStorageAtom);
  const { user, meId, isLoggedIn } = authStorage;

  const { client } = useGraphQLClient();

  const hasAuthToken = !!(client as any).options.headers.authorization;

  useGetMeQuery(
    client,
    {},
    {
      enabled: isLoggedIn && !meId && hasAuthToken,
      onSuccess: (data) => {
        setAuthStorage((prev) => {
          return {
            ...prev,
            role: data?.me?.role?.type,
            meId: data?.me?.documentId
          };
        });
      },
      onError: (error: any) => {
        setAuthStorage(authStorageInitialState);
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  );

  useGetUserQuery(
    client,
    { userId: meId as string },
    {
      enabled: !!meId,
      onSuccess: (data) => {
        setAuthStorage((prev) => {
          return {
            ...prev,
            user: data.usersPermissionsUser as UsersPermissionsUser
          };
        });
      },
      onError: (error: any) => {
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  );

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [from, navigate, user]);

  const {
    mutate: loginMutation,
    isLoading: loading,
    isError: mutationError
  } = useLoginMutation(client);

  const onSubmit = (values: FormData) => {
    // console.log("Success:", values);
    const { userName, password } = values;

    loginMutation(
      { identifier: userName, password: password },
      {
        onSuccess: (data) => {
          console.log("data :>> ", data);
          setAuthStorage({
            user: undefined,
            isLoggedIn: true,
            // meId: data?.login?.user?.id,
            token: data.login.jwt
          });
        },
        onError: (error: any) => {
          const _message = "Login error, please check your credentials.";
          // const _message = error.response.errors[0].message;
          console.log("_message :>> ", _message);
          setAuthStorage(authStorageInitialState);
          toast.error(_message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: {
          xs: "calc(100vh - 56px - 56px)",
          md: "calc(100vh - 69px - 56px)"
        },
        position: "relative",
        pb: 3
      }}
    >
      {isLoggedIn ? (
        <Loading />
      ) : (
        <Box
          bgcolor="background.default"
          p={2}
          width="100%"
          sx={{ borderRadius: 2, boxShadow: 10 }}
        >
          <Typography className="warning" sx={{ mb: 3 }}>
            Per la login usare la mail completa incluso <b>@mediacross.it</b>.
            La password non è la stessa che usate per accedere alla vostra
            postazione la mattina, se non la ricordate cliccate sul tasto <br />
            <b>Password dimenticata</b> per crearne una nuova.
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            width="100%"
          >
            <Controller
              name="userName"
              control={control}
              rules={{ required: "campo obbligatorio" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  required
                  fullWidth
                  label="Email"
                  autoComplete="email"
                  autoFocus
                  error={!!errors.userName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlined />
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
            {errors.userName && (
              <FormHelperText error>{errors.userName.message}</FormHelperText>
            )}
            <Controller
              name="password"
              control={control}
              rules={{ required: "campo obbligatorio" }}
              render={({ field }) => (
                <PasswordField
                  label="Password"
                  error={errors.password as any}
                  {...field}
                />
              )}
            />
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              loading={loading}
            >
              Log in
            </LoadingButton>
            <Grid container>
              <Grid item xs>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                >
                  Password dimenticata?
                </Link>
              </Grid>
              {/* <Grid item>
                  <Link href="#" variant="body2">
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid> */}
            </Grid>
            {mutationError && (
              <FormHelperText error>Error :( Riprova</FormHelperText>
            )}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default LoginPage;
